import * as Yup from "yup";
import { Product } from "./product";
import { ApiResponse } from "../utils/response";
import { getFromApi, postToApi } from "../utils/request";

export enum VolumeUnit {
  L = "l",
  CL = "cl",
}
export interface Batch {
  id: string;
  batchNumber: string;
  created: string;
  createdBy: string;
  productNumber: string;
  quantity: number;
  vintage: number;
  volume: number;
  volumeUnit: VolumeUnit;
  batchId: string;
  gtin: number;
  abv: number;
  additionalInformation: string;
  productImageOutdated: boolean;
  sampleRequested: boolean;
}
export interface BatchFormData {
  date: Date | null;
  product: Product | null;
  quantity: number | "";
  vintage: number | "";
  volume: number | "";
  volumeUnit: string;
  batchId: string;
  gtin: number | "";
  abv: number | "";
  additionalInformation: string;
  productImageOutdated: boolean;
  sampleRequested: boolean;
}

const convertToLitres = (volume: number, volumeUnit: string) =>
  volumeUnit === VolumeUnit.CL ? (volume || 0) / 100 : volume;

export const createNewBatch = async (batchData: BatchFormData, userName: string): Promise<ApiResponse<Batch>> => {
  try {
    const {
      date,
      product,
      quantity,
      vintage,
      volume,
      volumeUnit,
      batchId,
      gtin,
      abv,
      additionalInformation,
      productImageOutdated,
      sampleRequested,
    } = batchData;
    const batch = {
      date: date?.toISOString().substring(0, 10),
      created: new Date(),
      createdBy: userName,
      batchId,
      quantity,
      productNumber: product?.id,
      gtin,
      vintage,
      volume: convertToLitres(volume || 0, volumeUnit),
      volumeUnit: VolumeUnit.L,
      abv,
      additionalInformation,
      productImageOutdated,
      sampleRequested,
    };

    const castedBatch = castBatchDataForApi(batch);

    const response = await postToApi("/v1/batch", castedBatch);
    const data = await response.json();
    return { data };
  } catch (e) {
    throw new Error(e.message);
  }
};

export interface ListQueryParams {
  page?: number;
  pageSize: number;
  filters?: {
    date: Date;
  };
}
export const findBatches = async ({ page, pageSize, filters }: ListQueryParams): Promise<ApiResponse<Batch[]>> => {
  try {
    const { data, count, error } = await getFromApi<Batch[]>("/v1/batches", {
      page,
      pageSize,
      date: filters?.date?.toISOString().substr(0, 10),
    });
    if (error) {
      throw new Error(error);
    }
    return { data, count };
  } catch (e) {
    throw new Error(e.message);
  }
};

const VolumeUnitSchema = Yup.string().oneOf(["l", "cl"]).defined();
const batchSchemaApi = Yup.object({
  id: Yup.string().required(),
  batchNumber: Yup.number().required(),
  created: Yup.date().required(),
  createdBy: Yup.string().required(),
  productNumber: Yup.string().required(),
  quantity: Yup.number().required(),
  vintage: Yup.string(),
  volume: Yup.string().required(),
  volumeUnit: VolumeUnitSchema,
  batchId: Yup.string().required(),
  gtin: Yup.string().required(),
  abv: Yup.string().required(),
  additionalInformation: Yup.string(),
  productImageOutdated: Yup.boolean().required(),
  sampleRequested: Yup.boolean().required(),
  date: Yup.string().required(),
});

export interface IBatchApiRequest extends Yup.TypeOf<typeof batchSchemaApi> {}

function castBatchDataForApi(item: unknown): IBatchApiRequest {
  return batchSchemaApi.cast(item);
}
